// packages/sdk/src/credits/CreditsApi.ts
//
// `Host.credits` — the player-facing AI "creator" credits surface. When a game
// is player-billed (`billingMode: 'player'`), the signed-in player's creator
// credits are charged for the AI generation the game triggers. This namespace
// lets a game read the player's balance / plan / catalog and drive a paywall.
//
// NOTE: this is a DIFFERENT credit system from `Host.iap` (the in-game HC
// wallet). These are the AI creator credits (tiers creator|plus|pro|power|max).

import { RundotApiError } from '../errors'
import type { AiMessage, AiTextContent } from '../ai/AiApi'

/**
 * Canonical machine discriminator for creator-credits exhaustion. Mirrors the
 * server's `CREDITS_EXHAUSTED_CODE`: the host rejects an exhausted generation
 * with this `code`, and the SDK reconstructs the typed error from it. Single
 * source of truth — never write the literal elsewhere in the SDK.
 */
export const CREDITS_EXHAUSTED_CODE = 'CREDITS_EXHAUSTED'

export type Unsubscribe = () => void

/** Who AI generation in the current game is billed to. */
export type CreditsBilledTo = 'owner' | 'player'

/**
 * Informational context about how the current game bills AI generation. This is
 * ALWAYS available and NEVER gates any other call — the game decides what to
 * render from it.
 */
export interface CreditsBillingContext {
  billedTo: CreditsBilledTo
  /** True only when `billedTo === 'player'` AND player-charge enforcement is live. */
  playerChargesEnabled: boolean
}

/** Free-daily grant disclosure, so the game can render a reset countdown. */
export interface CreditFreeDailyInfo {
  dailyCredits: number
  availableCredits: number
  /** ISO timestamp of the next free-daily reset. */
  nextResetAt: string
}

export interface CreditBalance {
  /** Credits the player can spend right now (server `totalSpendable`). */
  available: number
  /** "available / total" denominator (server `totalIssued`). */
  total: number
  /** Free-daily grant info, or `null` when the player isn't eligible (paid plan). */
  freeDaily: CreditFreeDailyInfo | null
}

export interface CreditSubscription {
  status: 'active' | 'none'
  /** Paid SKU tier (NOT the effective/granted tier), or `null` when not subscribed. */
  tier: string | null
  monthlyCredits: number | null
  creditsRemaining: number | null
  /** ISO renewal/expiry timestamp, or `null`. */
  renewsAt: string | null
  willRenew: boolean
}

export interface CreditPlan {
  tier: string
  productId: string
  monthlyCredits: number
  rolloverDays: number
}

export interface CreditTopUpPack {
  productId: string
  credits: number
}

export interface CreditPlansCatalog {
  plans: CreditPlan[]
  /**
   * True when the subscription-plan catalog couldn't be resolved right now
   * (live-config outage). `plans` is then `[]` — treat it as "temporarily
   * unavailable, retry later", never as "no plans exist". `topUpPacks` and
   * `freeDailyCredits` are sourced independently and stay valid regardless.
   */
  plansUnavailable: boolean
  topUpPacks: CreditTopUpPack[]
  freeDailyCredits: number
}

export interface OpenPaywallOptions {
  /** Which surface to focus. `'topup'` is a phase-2 surface (currently no-op). */
  focus?: 'plans' | 'topup'
  /** Tier id to visually highlight in the plan picker. */
  highlightTier?: string
  /** Screen/route name for analytics attribution. */
  screenName?: string
}

export type CreditsPurchaseOutcome = 'purchased' | 'cancelled' | 'pending'

export interface CreditsPurchaseResult {
  outcome: CreditsPurchaseOutcome
  /** Refreshed balance after the paywall closed, or `null` if it couldn't be read. */
  balance: CreditBalance | null
}

export type GenerationCostEstimateKind =
  | 'image'
  | 'music'
  | 'sfx'
  | 'video'
  | 'sprite'
  | 'animate-sprite'
  | 'sprite-character-animate'
  | 'tts'
  | 'text'
  | 'upscale'

export interface GenerationCostEstimateRequest {
  kind: GenerationCostEstimateKind
  model?: string
  provider?: string
  mode?: string
  durationSeconds?: number
  imageSize?: string
  removeBackground?: boolean
  bgRemovalModel?: string
  generateAudio?: boolean
  resolution?: string
  text?: string
  messages?: AiMessage[]
  system?: string | AiTextContent[]
  maxTokens?: number
  maxCompletionTokens?: number
  /** Multiplies the quote for a batch of identical generations (default 1).
   *  A batch of N scene clips is `{ kind: 'video', …, quantity: N }`. */
  quantity?: number
  /** `upscale` kind: the source media's content type — `'image/*'` prices the
   *  flat image rate, `'video/*'` per-second by `resolution` ('720p'|'1080p')
   *  × `durationSeconds`. Required unless `outputMp` is set. */
  contentType?: string
  /** `upscale` kind: output megapixels — selects the tiered Topaz
   *  advanced-upscale pricing instead of the flat rate. */
  outputMp?: number
}

export interface GenerationCostEstimate {
  kind: GenerationCostEstimateKind
  credits: number
  lowCredits: number
  highCredits: number
  lowerPercent: number
  upperPercent: number
  exact: boolean
  reason: string
}

/** Construction payload for {@link CreditsExhaustedError}. */
export interface CreditsExhaustedErrorInfo {
  billedTo: CreditsBilledTo
  paywallShown: boolean
  paywallOutcome: CreditsPurchaseOutcome | null
}

/**
 * Thrown from AI generation calls (ai / imageGen / videoGen) when the
 * player-billed game runs the player out of creator credits and the host did
 * not (or could not) resolve the deficit. `paywallShown`/`paywallOutcome`
 * describe what the host did before giving up — see `setAutoPaywallOnExhaustion`
 * / `setAutoRetryOnPurchase`.
 */
export class CreditsExhaustedError extends Error {
  readonly code = CREDITS_EXHAUSTED_CODE
  readonly billedTo: CreditsBilledTo
  readonly paywallShown: boolean
  /** `null` when `paywallShown === false`. */
  readonly paywallOutcome: CreditsPurchaseOutcome | null

  constructor(message: string, info: CreditsExhaustedErrorInfo) {
    super(message)
    this.name = 'CreditsExhaustedError'
    this.billedTo = info.billedTo
    this.paywallShown = info.paywallShown
    this.paywallOutcome = info.paywallOutcome
  }
}

/** True iff `err` is (or carries the wire signature of) a credits-exhaustion
 *  error. Derived from {@link asCreditsExhaustedError} so the predicate and the
 *  narrowing can never disagree. */
export function isCreditsExhaustedError(err: unknown): boolean {
  return asCreditsExhaustedError(err) !== null
}

/**
 * Reconstruct a {@link CreditsExhaustedError} from a transport error. The host
 * rejects the RPC with `code === 'CREDITS_EXHAUSTED'` and a JSON-encoded
 * `detail` carrying `{ billedTo, paywallShown, paywallOutcome }`; this turns
 * that wire shape back into a typed error. Returns `null` for any other error.
 */
export function asCreditsExhaustedError(err: unknown): CreditsExhaustedError | null {
  if (err instanceof CreditsExhaustedError) return err
  if (err === null || typeof err !== 'object') return null
  if ((err as { code?: unknown }).code !== CREDITS_EXHAUSTED_CODE) return null

  // Default to 'owner' on a missing/malformed payload — the same fail-safe the
  // host bridge and server use. Only an explicit 'player' attribution should
  // surface player paywall UX; never one we had to guess from an incomplete wire
  // shape, or an owner-billed exhaustion gets mislabelled as the player's to fix.
  let billedTo: CreditsBilledTo = 'owner'
  let paywallShown = false
  let paywallOutcome: CreditsPurchaseOutcome | null = null

  const detail = (err as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail.length > 0) {
    try {
      const parsed = JSON.parse(detail) as Partial<CreditsExhaustedErrorInfo>
      if (parsed.billedTo === 'owner' || parsed.billedTo === 'player') billedTo = parsed.billedTo
      if (typeof parsed.paywallShown === 'boolean') paywallShown = parsed.paywallShown
      if (
        parsed.paywallOutcome === 'purchased' ||
        parsed.paywallOutcome === 'cancelled' ||
        parsed.paywallOutcome === 'pending' ||
        parsed.paywallOutcome === null
      ) {
        paywallOutcome = parsed.paywallOutcome
      }
    } catch {
      // Fall through with defaults — a malformed detail must not mask the error.
    }
  }

  const message =
    err instanceof RundotApiError || err instanceof Error
      ? err.message
      : 'Creator credits exhausted'
  return new CreditsExhaustedError(message, { billedTo, paywallShown, paywallOutcome })
}

/**
 * Await an RPC generation call, re-throwing creator-credits exhaustion as the
 * typed {@link CreditsExhaustedError} and passing every other error through
 * untouched. The single place the generation namespaces (ai / imageGen /
 * videoGen) funnel through, so the mapping can't drift between them.
 */
export async function mapCreditsExhaustion<T>(call: Promise<T>): Promise<T> {
  try {
    return await call
  } catch (err) {
    throw asCreditsExhaustedError(err) ?? err
  }
}

export interface CreditsApi {
  /** Informational ONLY — never gates any other call. */
  getBillingContext(): Promise<CreditsBillingContext>

  getBalance(): Promise<CreditBalance>
  getSubscription(): Promise<CreditSubscription>
  getPlans(): Promise<CreditPlansCatalog>
  estimateGenerationCost(request: GenerationCostEstimateRequest): Promise<GenerationCostEstimate>

  /**
   * Open the host's combined paywall (plan upgrade now; top-up packs in a later
   * release). Resolves once the player closes it. Reuses the same host UI the
   * exhaustion auto-paywall drives.
   */
  openPaywall(options?: OpenPaywallOptions): Promise<CreditsPurchaseResult>

  /**
   * When the player exhausts credits mid-generation, automatically open the
   * paywall (default `true`). Synchronous: local SDK state pushed to the host
   * fire-and-forget.
   */
  setAutoPaywallOnExhaustion(enabled: boolean): void
  getAutoPaywallOnExhaustion(): boolean
  /**
   * After an exhaustion paywall purchase, automatically retry the original
   * generation once and resolve it with the result (default `true`). Synchronous.
   */
  setAutoRetryOnPurchase(enabled: boolean): void
  getAutoRetryOnPurchase(): boolean

  /**
   * Fires whenever the player's spendable balance changes — both when they buy
   * credits at the paywall and when they spend credits on AI generation.
   *
   * Rapid changes are coalesced, so a burst of generations finishing together
   * may deliver a single update; that update always reflects the final balance.
   *
   * It does NOT currently fire for free-daily resets, for top-ups made outside
   * the game, or for streaming AI completions.
   */
  onBalanceChanged(listener: (balance: CreditBalance) => void): Unsubscribe
}
